/**
 * BUSINESS LAYER (BL): RUN TO GOAL (TIRO A GOL V2)
 * Capa de lógica de negocio, validaciones preventivas, precarga de assets gráficos en RAM,
 * normalización de telemetría de N zonas y cálculo físico/matemático de avance de corredores.
 * 
 * @module utilities/BL/RunToGoal
 */

import {
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    SaveThemeDL,
    CloneThemeDL,
    DeleteThemeDL,
    AssignThemeToRoomDL,
    GetActiveThemeForRoomDL,
    UploadMediaDL,
    SeedPresetsDL,
    CreateRunToGoalV2MatchDL,
    StartRunToGoalV2MatchDL,
    ResetRunToGoalV2MatchDL,
    FinishRunToGoalV2MatchEarlyDL,
    ShowRunToGoalV2FrontPageDL,
    AddTapsToRunToGoalV2ZoneDL
} from '../DL/RunToGoal';

import { SetScreenV2MainDynamic } from './ScreenV2';

// =========================================================================
// NORMALIZACIÓN DE URLs Y MOTOR DE PRECARGA EN MEMORIA RAM
// =========================================================================

/**
 * Normaliza y resuelve URLs de recursos multimedia para precarga y renderizado sin parpadeos.
 * @param {string|null} mediaPath - URL relativa, absoluta o Data URI.
 * @returns {string} URL absoluta normalizada.
 */
export const ResolveMediaUrl = (mediaPath) => {
    if (!mediaPath || typeof mediaPath !== 'string') return '';
    const trimmed = mediaPath.trim();
    if (!trimmed || /^(javascript|vbscript):/i.test(trimmed)) return '';
    if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return trimmed;

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('//')) {
        return trimmed;
    }

    const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed.replace(/^\.\//, '')}`;

    // Si es un recurso estático del frontend web (/img/...)
    if (cleanPath.startsWith('/img/')) {
        const webOrigin = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : '';
        return `${webOrigin}${cleanPath}`;
    }

    // Si es un recurso subido dinámicamente al backend (/uploads/...) o cualquier otro relativo de API
    const apiBase = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const hostBase = apiBase.replace(/\/api\/?$/i, '').replace(/\/+$/, '');
    return `${hostBase}${cleanPath}`;
};

export const getResolvedMediaUrl = ResolveMediaUrl;

/**
 * Descarga y precarga de forma concurrente una lista de URLs de imágenes en la memoria RAM del navegador.
 * Previene pantallas blancas / flickering e incluye timeout de seguridad y deduplicación.
 * 
 * @param {Array<string>} [urls=[]] - Colección de URLs a precargar.
 * @returns {Promise<{total: number, loaded: number, failed: number, results: Array<{url: string, success: boolean}>}>}
 */
export const PreloadImages = async (urls = []) => {
    if (!urls || !Array.isArray(urls)) {
        return { total: 0, loaded: 0, failed: 0, results: [] };
    }

    const validUrls = Array.from(new Set(
        urls
            .filter((url) => typeof url === 'string' && url.trim().length > 0)
            .map((url) => ResolveMediaUrl(url))
            .filter((url) => url && !url.startsWith('data:') && !url.startsWith('blob:'))
    ));

    if (validUrls.length === 0) {
        return { total: 0, loaded: 0, failed: 0, results: [] };
    }

    const preloadPromises = validUrls.map((imageUrl) => {
        return new Promise((resolve) => {
            let settled = false;
            const img = new Image();

            const onSettled = (success) => {
                if (settled) return;
                settled = true;
                if (safetyTimeoutId) clearTimeout(safetyTimeoutId);
                img.onload = null;
                img.onerror = null;
                img.onabort = null;
                resolve({ url: imageUrl, success: Boolean(success) });
            };

            const safetyTimeoutId = setTimeout(() => {
                onSettled(false);
            }, 8000);

            img.onload = () => onSettled(true);
            img.onerror = () => onSettled(false);
            img.onabort = () => onSettled(false);
            img.src = imageUrl;
        });
    });

    const settledResults = await Promise.allSettled(preloadPromises);
    const results = settledResults.map((item, idx) => {
        if (item.status === 'fulfilled') {
            return item.value;
        }
        return { url: validUrls[idx], success: false };
    });

    const loadedCount = results.filter((r) => r.success).length;
    return {
        total: results.length,
        loaded: loadedCount,
        failed: results.length - loadedCount,
        results
    };
};

/**
 * Extrae y precarga en memoria RAM todos los recursos gráficos definidos en un tema de Tiro a Gol.
 * Incluye portada, logotipos, pista/cancha, sprites de zonas en zonesConfigJson, destellos, balón y outro.
 * 
 * @param {Object|null} theme - Objeto DTO del tema visual de Tiro a Gol.
 * @returns {Promise<{total: number, loaded: number, failed: number, results: Array<{url: string, success: boolean}>}>}
 */
export const PreloadRunToGoalAssets = async (theme) => {
    if (!theme || typeof theme !== 'object') {
        return { total: 0, loaded: 0, failed: 0, results: [] };
    }

    const rawUrls = [
        // Portada
        theme.coverBackgroundUrl || theme.CoverBackgroundUrl,
        theme.brandLogoUrl || theme.BrandLogoUrl,
        theme.sponsorLogoUrl || theme.SponsorLogoUrl,
        // Pista / Cancha
        theme.trackBackgroundUrl || theme.TrackBackgroundUrl,
        theme.ballImageUrl || theme.BallImageUrl || '/img/games/runtogoal/balon.png',
        '/img/games/runtogoal/balon-grande.png',
        // Animación de Outro / Victoria
        theme.outroBackgroundUrl || theme.OutroBackgroundUrl,
        theme.outroElementUrl || theme.OutroElementUrl,
        // Vista Aficionado
        theme.fanBackgroundUrl || theme.FanBackgroundUrl,
        theme.fanButtonIcon || theme.FanButtonIcon
    ];

    // Extraer avatares, destellos y ganador de las zonas dinámicas
    let zonesArray = [];
    if (Array.isArray(theme.zones) && theme.zones.length > 0) {
        zonesArray = theme.zones;
    } else if (Array.isArray(theme.Zones) && theme.Zones.length > 0) {
        zonesArray = theme.Zones;
    } else {
        const rawJson = theme.zonesConfigJson || theme.ZonesConfigJson;
        if (typeof rawJson === 'string' && rawJson.trim().length > 0) {
            try {
                const parsed = JSON.parse(rawJson);
                if (Array.isArray(parsed)) zonesArray = parsed;
            } catch (e) {
                // Silencioso si no es JSON válido
            }
        }
    }

    zonesArray.forEach((z) => {
        if (!z) return;
        if (z.avatarUrl || z.AvatarUrl) rawUrls.push(z.avatarUrl || z.AvatarUrl);
        if (z.sparkUrl || z.SparkUrl) rawUrls.push(z.sparkUrl || z.SparkUrl);
        if (z.winnerAvatarUrl || z.WinnerAvatarUrl) rawUrls.push(z.winnerAvatarUrl || z.WinnerAvatarUrl);
    });

    return await PreloadImages(rawUrls);
};

// =========================================================================
// NORMALIZACIÓN DE TELEMETRÍA Y CÁLCULOS MATEMÁTICOS DE CARRERA
// =========================================================================

/**
 * Normaliza y consolida la telemetría en vivo de zonasProgress con la configuración visual del tema activo.
 * Mapea cada zona a su avatar, color, nombre visual y total de taps acumulados.
 * 
 * @param {Array<Object>} [zonesProgress=[]] - Arreglo de progreso emitido por WebSocket.
 * @param {Object|null} [theme=null] - Tema activo con zonesConfigJson o arreglo de zonas.
 * @returns {Array<Object>} Arreglo estructurado de zonas listas para renderizado.
 */
export const NormalizeZonesProgress = (zonesProgress = [], theme = null) => {
    let themeZones = [];
    if (theme) {
        if (Array.isArray(theme.zones) && theme.zones.length > 0) {
            themeZones = theme.zones;
        } else if (Array.isArray(theme.Zones) && theme.Zones.length > 0) {
            themeZones = theme.Zones;
        } else {
            const rawJson = theme.zonesConfigJson || theme.ZonesConfigJson;
            if (typeof rawJson === 'string' && rawJson.trim().length > 0) {
                try {
                    const parsed = JSON.parse(rawJson);
                    if (Array.isArray(parsed)) themeZones = parsed;
                } catch (e) {
                    console.warn('[NormalizeZonesProgress] Error al parsear zonesConfigJson:', e);
                }
            }
        }
    }

    const defaultSkins = {
        'zona1': { name: 'Xolo Mayor Rojo', color: '#ef4444', avatarUrl: '/img/games/runtogoal/xolo-caricatura-1.png', sparkUrl: '/img/games/runtogoal/destellos-rojos.png', winnerAvatarUrl: '/img/games/runtogoal/xolo-caricatura-1.png' },
        'zona2': { name: 'Xolo Mayor Blanco', color: '#3b82f6', avatarUrl: '/img/games/runtogoal/xolo-caricatura-2.png', sparkUrl: '/img/games/runtogoal/destellos-blancos.png', winnerAvatarUrl: '/img/games/runtogoal/xolo-caricatura-2.png' },
        'zona3': { name: 'Zona 3', color: '#10b981', avatarUrl: '/img/games/runtogoal/xolo-caricatura-1.png', sparkUrl: '/img/games/runtogoal/destellos-rojos.png', winnerAvatarUrl: '/img/games/runtogoal/xolo-caricatura-1.png' },
        'zona4': { name: 'Zona 4', color: '#f59e0b', avatarUrl: '/img/games/runtogoal/xolo-caricatura-2.png', sparkUrl: '/img/games/runtogoal/destellos-blancos.png', winnerAvatarUrl: '/img/games/runtogoal/xolo-caricatura-2.png' }
    };

    const themeZoneMap = new Map();
    themeZones.forEach((tz) => {
        const id = (tz.zoneId || tz.ZoneId || tz.id || tz.name || '').toLowerCase().trim();
        if (id) {
            themeZoneMap.set(id, {
                zoneId: tz.zoneId || tz.ZoneId || id,
                name: tz.name || tz.Name || id,
                color: tz.color || tz.Color || '#3b82f6',
                avatarUrl: ResolveMediaUrl(tz.avatarUrl || tz.AvatarUrl || ''),
                sparkUrl: ResolveMediaUrl(tz.sparkUrl || tz.SparkUrl || ''),
                winnerAvatarUrl: ResolveMediaUrl(tz.winnerAvatarUrl || tz.WinnerAvatarUrl || tz.avatarUrl || tz.AvatarUrl || '')
            });
        }
    });

    // Consolidar taps agrupando por zoneIdentifier normalizado
    const groupedTaps = new Map();
    if (Array.isArray(zonesProgress)) {
        zonesProgress.forEach((item) => {
            if (!item) return;
            const rawId = (item.zoneIdentifier || item.zoneId || item.zone || item.ZoneIdentifier || 'unknown').toLowerCase().trim();
            const currentTaps = Number(item.totalTaps || item.taps || item.TotalTaps || 0) || 0;
            groupedTaps.set(rawId, (groupedTaps.get(rawId) || 0) + currentTaps);
        });
    }

    const resultZones = [];

    // Si el tema tiene zonas definidas, el juego se rige ESTRICTAMENTE por esas zonas
    if (themeZones.length > 0) {
        themeZones.forEach((tz, index) => {
            const id = (tz.zoneId || tz.ZoneId || tz.id || `zona${index + 1}`).toLowerCase().trim();
            const themeConfig = themeZoneMap.get(id);
            const totalTaps = groupedTaps.get(id) || 0;
            const fallbackConfig = defaultSkins[id] || defaultSkins['zona1'];

            resultZones.push({
                zoneIdentifier: themeConfig?.zoneId || tz.zoneId || tz.ZoneId || id,
                zoneId: themeConfig?.zoneId || tz.zoneId || tz.ZoneId || id,
                name: themeConfig?.name || fallbackConfig?.name || `Zona ${index + 1}`,
                color: themeConfig?.color || fallbackConfig?.color || (index % 2 === 0 ? '#ef4444' : '#3b82f6'),
                avatarUrl: themeConfig?.avatarUrl || ResolveMediaUrl(fallbackConfig?.avatarUrl || ''),
                sparkUrl: themeConfig?.sparkUrl || ResolveMediaUrl(fallbackConfig?.sparkUrl || ''),
                winnerAvatarUrl: themeConfig?.winnerAvatarUrl || ResolveMediaUrl(fallbackConfig?.winnerAvatarUrl || ''),
                totalTaps,
                taps: totalTaps
            });
        });
        return resultZones.sort((a, b) => String(a.zoneId).localeCompare(String(b.zoneId)));
    }

    if (groupedTaps.size === 0) {
        ['zona1', 'zona2'].forEach((zoneKey) => {
            const fallbackConfig = defaultSkins[zoneKey];
            resultZones.push({
                zoneIdentifier: zoneKey,
                zoneId: zoneKey,
                name: fallbackConfig.name,
                color: fallbackConfig.color,
                avatarUrl: ResolveMediaUrl(fallbackConfig.avatarUrl),
                sparkUrl: ResolveMediaUrl(fallbackConfig.sparkUrl),
                winnerAvatarUrl: ResolveMediaUrl(fallbackConfig.winnerAvatarUrl),
                totalTaps: 0,
                taps: 0
            });
        });
        return resultZones;
    }

    groupedTaps.forEach((totalTaps, zoneKey) => {
        const fallbackConfig = defaultSkins[zoneKey] || defaultSkins['zona1'];
        resultZones.push({
            zoneIdentifier: zoneKey,
            zoneId: zoneKey,
            name: fallbackConfig?.name || zoneKey.toUpperCase(),
            color: fallbackConfig?.color || '#3b82f6',
            avatarUrl: ResolveMediaUrl(fallbackConfig?.avatarUrl || ''),
            sparkUrl: ResolveMediaUrl(fallbackConfig?.sparkUrl || ''),
            winnerAvatarUrl: ResolveMediaUrl(fallbackConfig?.winnerAvatarUrl || ''),
            totalTaps,
            taps: totalTaps
        });
    });

    return resultZones.sort((a, b) => String(a.zoneId).localeCompare(String(b.zoneId)));
};

/**
 * Calcula la posición de avance porcentual relativo del líder con el reloj y desfase monótono de las demás zonas.
 * Garantiza que los corredores nunca retrocedan visualmente durante una carrera activa.
 * 
 * @param {Array<Object>} [dynamicZones=[]] - Zonas procesadas.
 * @param {number} [highestTaps=0] - Total máximo de taps alcanzado por el líder.
 * @param {number} [timePct=0] - Porcentaje de tiempo transcurrido (0 - 100).
 * @param {Object|React.MutableRefObject|null} [maxPositionsHistoryRef=null] - Referencia o mapa acumulador de máximos.
 * @param {number} [maxVisualLagPct=25] - Porcentaje máximo de rezago visual para las zonas perdedoras.
 * @returns {Array<Object>} Colección enriquecida con `positionPct`, `finalPosition` y `finishGap`.
 */
export const CalculateRunnerPositions = (
    dynamicZones = [],
    highestTaps = 0,
    timePct = 0,
    maxPositionsHistoryRef = null,
    maxVisualLagPct = 25
) => {
    if (!Array.isArray(dynamicZones)) return [];

    const safeTimePct = Math.min(100, Math.max(0, Number(timePct) || 0));
    const safeHighestTaps = Math.max(0, Number(highestTaps) || 0);
    const safeMaxLag = Number(maxVisualLagPct) >= 0 ? Number(maxVisualLagPct) : 25;

    const historyObj = (maxPositionsHistoryRef && typeof maxPositionsHistoryRef === 'object')
        ? (maxPositionsHistoryRef.current !== undefined ? maxPositionsHistoryRef.current : maxPositionsHistoryRef)
        : null;

    return dynamicZones.map((zoneItem, index) => {
        const zoneId = (zoneItem.zoneIdentifier || zoneItem.zoneId || zoneItem.zone || `zone-${index}`).toLowerCase().trim();
        const taps = Number(zoneItem.totalTaps || zoneItem.taps || 0) || 0;

        let finalPosition = safeTimePct;

        if (safeHighestTaps > 0) {
            const tapsDeficit = safeHighestTaps - taps;
            const lagRatio = tapsDeficit / safeHighestTaps;
            finalPosition -= (lagRatio * safeMaxLag);
        }

        if (historyObj && typeof historyObj === 'object') {
            const prevMax = Number(historyObj[zoneId]) || 0;
            if (finalPosition < prevMax) {
                finalPosition = prevMax;
            } else {
                historyObj[zoneId] = finalPosition;
            }
        }

        finalPosition = Math.min(100, Math.max(0, finalPosition));
        const finishGap = 20 - (index * 4);

        return {
            ...zoneItem,
            zoneId,
            positionPct: finalPosition,
            finalPosition,
            finishGap
        };
    });
};

// =========================================================================
// ENVOLTORIOS DE NEGOCIO PARA TEMAS VISUALES
// =========================================================================

/**
 * Consulta los temas de Tiro a Gol para una sala u operador.
 */
export const GetThemesBL = async (roomId = null, operatorId = null) => {
    return await GetThemesDL(roomId, operatorId);
};

/**
 * Obtiene el detalle de un tema por ID.
 */
export const GetThemeByIdBL = async (id) => {
    if (!id) return { success: false, message: 'El ID del tema es obligatorio.' };
    return await GetThemeByIdDL(id);
};

/**
 * Crea un nuevo tema personalizado de Tiro a Gol con validaciones previas.
 */
export const CreateThemeBL = async (themeData) => {
    if (!themeData || !themeData.name || !themeData.name.trim()) {
        return { success: false, message: 'El nombre del tema es obligatorio.' };
    }
    return await CreateThemeDL(themeData);
};

/**
 * Actualiza un tema existente de Tiro a Gol.
 */
export const UpdateThemeBL = async (id, themeData) => {
    if (!id) return { success: false, message: 'El ID del tema es obligatorio.' };
    if (!themeData || !themeData.name || !themeData.name.trim()) {
        return { success: false, message: 'El nombre del tema es obligatorio.' };
    }
    return await UpdateThemeDL(id, themeData);
};

/**
 * Guarda un tema (creando o actualizando según corresponda).
 */
export const SaveThemeBL = async (themeData) => {
    if (!themeData || !themeData.name || !themeData.name.trim()) {
        return { success: false, message: 'El nombre del tema es obligatorio.' };
    }
    return await SaveThemeDL(themeData);
};

/**
 * Clona un tema existente como una nueva copia editable.
 */
export const CloneThemeBL = async (id, newName = null) => {
    if (!id) return { success: false, message: 'El ID del tema es obligatorio.' };
    return await CloneThemeDL(id, newName);
};

/**
 * Elimina un tema personalizado.
 */
export const DeleteThemeBL = async (id) => {
    if (!id) return { success: false, message: 'El ID del tema es obligatorio.' };
    return await DeleteThemeDL(id);
};

/**
 * Asigna un tema como activo para una sala.
 */
export const AssignThemeToRoomBL = async (themeId, roomId) => {
    if (!themeId || !roomId) {
        return { success: false, message: 'El themeId y roomId son obligatorios.' };
    }
    return await AssignThemeToRoomDL(themeId, roomId);
};

/**
 * Obtiene el tema activo correspondiente a una sala.
 */
export const GetActiveThemeForRoomBL = async (roomId) => {
    if (!roomId) return { success: false, message: 'El roomId es obligatorio.' };
    return await GetActiveThemeForRoomDL(roomId);
};

/**
 * Sube y optimiza un medio gráfico para Tiro a Gol.
 */
export const UploadMediaBL = async (file, category = null, fieldName = null) => {
    if (!file) return { success: false, message: 'No se ha seleccionado ningún archivo.' };
    return await UploadMediaDL(file, category, fieldName);
};

/**
 * Registra manualmente de forma idempotente los 3 temas preset de sistema en backend.
 */
export const SeedPresetsBL = async () => {
    return await SeedPresetsDL();
};

// =========================================================================
// ENVOLTORIOS DE NEGOCIO PARA PARTIDAS EN TIEMPO REAL V2
// =========================================================================

/**
 * Solicita al servidor la creación de una nueva partida V2 basada en tiempo con tema y sala.
 * @param {Object} matchConfiguration - Configuración de la partida.
 * @param {number} matchConfiguration.matchDurationSeconds - Segundos de duración.
 * @param {number} matchConfiguration.tapsPerTick - Frecuencia de envío por el celular.
 * @param {number} matchConfiguration.minimumUpdateMilliseconds - Frecuencia de actualización en pantalla.
 * @param {number|null} [matchConfiguration.themeId=null] - ID del tema visual.
 * @param {string|null} [matchConfiguration.roomId=null] - GUID de la sala.
 * @param {string|null} [matchConfiguration.operatorId=null] - GUID del operador.
 * @returns {Promise<Object>} Resultado de la creación.
 */
export const CreateRunToGoalV2Match = async (matchConfiguration = {}) => {
    return await CreateRunToGoalV2MatchDL(matchConfiguration);
};
export const CreateRunToGoalV2MatchBL = CreateRunToGoalV2Match;

/**
 * Da la orden al servidor para iniciar el cronómetro oficial de la partida.
 * @param {string} matchIdentifier - Identificador de la partida.
 */
export const StartRunToGoalV2Match = async (matchIdentifier) => {
    if (!matchIdentifier) return { success: false, message: 'El identificador de partida es obligatorio.' };
    return await StartRunToGoalV2MatchDL(matchIdentifier);
};
export const StartRunToGoalV2MatchBL = StartRunToGoalV2Match;

/**
 * Da la orden al servidor para resetear la partida a estado Waiting.
 * @param {string} matchIdentifier - Identificador de la partida.
 */
export const ResetRunToGoalV2Match = async (matchIdentifier) => {
    if (!matchIdentifier) return { success: false, message: 'El identificador de partida es obligatorio.' };
    return await ResetRunToGoalV2MatchDL(matchIdentifier);
};
export const ResetRunToGoalV2MatchBL = ResetRunToGoalV2Match;

/**
 * Da la orden al servidor para terminar el juego anticipadamente y calcular al ganador actual.
 * @param {string} matchIdentifier - Identificador de la partida.
 */
export const FinishRunToGoalV2MatchEarly = async (matchIdentifier) => {
    if (!matchIdentifier) return { success: false, message: 'El identificador de partida es obligatorio.' };
    return await FinishRunToGoalV2MatchEarlyDL(matchIdentifier);
};
export const FinishRunToGoalV2MatchEarlyBL = FinishRunToGoalV2MatchEarly;

/**
 * Dispara la señal para mostrar la portada de Tiro a Gol en pantalla gigante.
 */
export const ShowRunToGoalV2FrontPage = async (roomId = null, themeId = null, screenId = 1) => {
    try {
        const res = await ShowRunToGoalV2FrontPageDL(roomId, themeId);
        try {
            await SetScreenV2MainDynamic(screenId, 'RunToGoalFrontPage');
        } catch (e) {
            // Silencioso si ScreenV2 no está activo
        }
        return res;
    } catch (error) {
        return { success: false, message: 'Error de red al proyectar la portada.' };
    }
};
export const ShowRunToGoalV2FrontPageBL = ShowRunToGoalV2FrontPage;

/**
 * Función silenciosa que usan los celulares para enviar ráfagas de taps.
 * @param {Object} tapData - Información de los taps a enviar.
 */
export const AddTapsToRunToGoalV2Zone = async ({ matchIdentifier, zoneIdentifier, tapsObtained }) => {
    if (!matchIdentifier || !zoneIdentifier) return { success: false };
    return await AddTapsToRunToGoalV2ZoneDL({ matchIdentifier, zoneIdentifier, tapsObtained });
};
export const AddTapsToRunToGoalV2ZoneBL = AddTapsToRunToGoalV2Zone;

// =========================================================================
// RE-EXPORTACIÓN DE MÉTODOS CRUD
// =========================================================================

export {
    GetThemesBL as GetThemes,
    GetThemeByIdBL as GetThemeById,
    CreateThemeBL as CreateTheme,
    UpdateThemeBL as UpdateTheme,
    SaveThemeBL as SaveTheme,
    CloneThemeBL as CloneTheme,
    DeleteThemeBL as DeleteTheme,
    AssignThemeToRoomBL as AssignThemeToRoom,
    GetActiveThemeForRoomBL as GetActiveThemeForRoom,
    UploadMediaBL as UploadMedia,
    SeedPresetsBL as SeedPresets
};